import { colors as CropCircleColors } from '../components/crop-circle/tokens/colors';

/**
 * Legacy colors - Valores originales
 * Compatibility is maintained when CROP_CIRCLE_REDESIGN is disabled
 */
const LegacyColors = {
  primary: '#F47A20', // graphic/gutton/primary action
  primaryDark: '#B25000', // primary orange on gray background
  primaryWithAlpha: 'rgba(244, 122, 32, 0.1)', // hover style; this is when something is active and clicked
  primaryLight: '#FFEADA',

  white: '#fff',
  black: '#000',
  blackText: '#19191C', // used for text
  blackWithAlpha: 'rgba(0, 0, 0, 0.1)', // hover on the grids (web, activity list)
  blackInactive: 'rgba(0, 0, 0, 0.75)', // new issue modal background (overlay)
  blackOverlay: 'rgba(0, 0, 0, 0.3)', // used for dropdown overlay
  whiteOverlay: 'rgba(255, 255, 255, 0.9)', // new issue modal background (overlay)

  primaryGrayBackground: '#F2F1F6', // used in the background of the app
  grayText: '#6c6c70', // used for card text
  grayFill: '#8e8e93', // used for card info blocks & button
  graySpacer: '#D8D8DC', // used for horizontal spacer & right border
  grayDivider: '#AEAEB2', // used for card dividers
  grayButtonBorder: '#AEAEB2', // used for button borders
  grayDisabled: '#aeaeb2',
  primaryGrayHeader: '#D8D8DC',
  secondaryGrayHeader: '#E9E9EF', // aka cellHover, also used in assets gray divider header
  secondaryGreyBorder: '#D8D8D8',
  secondaryGreyBackground: '#FAFAFA',

  unreadBackground: '#FFFAE6', // aka cell-selected
  chipBackground: '#FDE3D0',
  redBackground: '#FFE3DF',

  danger: '#ff3b30',
  warning: '#ffcc00',
  satisfactory: '#34c759',
  info: '#007aff',

  inactive: 'rgba(255, 59, 48, 0.35)',
  active: '#ecfdf3',
  orangeActive: '#FFE1C6',
  orangeActiveHovered: '#FFC795',
  orangeDark: '#883A00',

  secondaryContainer: 'transparent',
  tabActive: '#000', // replace with black
  tabInactive: '#6c6c70', // replace with grayText

  redText: '#C12219',

  // issue severities
  red: '#FF3B30',
  yellow: '#FFCC00',
  green: '#34C759',
  blue: '#007AFF',
  blueLight: '#cfeafc',
  purple: '#7230FF',
  darkPink: '#C10097',

  // mentions list
  mentionName: '#242424',
  mentionEmail: '#616161',

  ...CropCircleColors,
};

/**
 * Crop Circle colors - New colors in the design system
 * Mapped to the same keys as legacy for full compatibility
 * Enabled when CROP_CIRCLE_REDESIGN feature flag is enabled
 */
const CropCircleColorsMap = {
  ...CropCircleColors,
  primary: CropCircleColors.brand.core, // #FF5505
  primaryDark: CropCircleColors.orange[600], // #C44A0A
  primaryWithAlpha: `${CropCircleColors.brand.core}1A`, // rgba(255, 85, 5, 0.1)
  primaryLight: CropCircleColors.orange[100], // #FFF0E5

  white: CropCircleColors.brand.white, // #FCFCFC
  black: CropCircleColors.brand.black, // #020202
  blackText: CropCircleColors.system.textPrimary, // #2F2F2F
  blackWithAlpha: `${CropCircleColors.neutral[800]}1A`, // rgba(2, 2, 2, 0.1)
  blackInactive: `${CropCircleColors.neutral[800]}BF`, // rgba(2, 2, 2, 0.75)
  blackOverlay: `${CropCircleColors.neutral[800]}4D`, // rgba(2, 2, 2, 0.3)
  whiteOverlay: `${CropCircleColors.brand.white}E6`, // rgba(252, 252, 252, 0.9)

  primaryGrayBackground: CropCircleColors.neutral[200], // #F3F3F3
  grayText: CropCircleColors.system.textSecondary, // #6C6C6C
  grayFill: CropCircleColors.system.muted, // #9FA6B2
  graySpacer: CropCircleColors.neutral[300], // #D8D8D8
  grayDivider: CropCircleColors.neutral[400], // #B4B4B4
  grayButtonBorder: CropCircleColors.system.borderDefault, // #D8D8D8
  grayDisabled: CropCircleColors.system.disabled, // #CCCCCC
  primaryGrayHeader: CropCircleColors.neutral[300], // #D8D8D8
  secondaryGrayHeader: CropCircleColors.slate[100], // #ECEEF1
  secondaryGreyBorder: CropCircleColors.neutral[300], // #D8D8D8
  secondaryGreyBackground: CropCircleColors.neutral[100], // #FCFCFC

  unreadBackground: CropCircleColors.semantic.warningLight, // #FFF4C1
  chipBackground: CropCircleColors.orange[200], // #FFD7B0
  redBackground: CropCircleColors.semantic.errorLight, // #FFD9D2

  danger: CropCircleColors.semantic.error, // #E24D2A
  warning: CropCircleColors.semantic.warning, // #F9C431
  satisfactory: CropCircleColors.semantic.success, // #36B37E
  info: CropCircleColors.semantic.info, // #4A90E2

  inactive: `${CropCircleColors.semantic.error}59`, // rgba(226, 77, 42, 0.35)
  active: CropCircleColors.semantic.successLight, // #D3F4E7
  orangeActive: CropCircleColors.orange[200], // #FFD7B0
  orangeActiveHovered: CropCircleColors.orange[300], // #FFB368
  orangeDark: CropCircleColors.orange[700], // #872D05

  secondaryContainer: 'transparent',
  tabActive: CropCircleColors.neutral[800], // #020202
  tabInactive: CropCircleColors.system.textSecondary, // #6C6C6C

  redText: CropCircleColors.semantic.errorDark, // #B32A12

  // issue severities
  red: CropCircleColors.semantic.error, // #E24D2A
  yellow: CropCircleColors.semantic.warning, // #F9C431
  green: CropCircleColors.semantic.success, // #36B37E
  blue: CropCircleColors.semantic.info, // #4A90E2
  blueLight: CropCircleColors.semantic.infoLight, // #D0E5F9
  purple: CropCircleColors.label.violet, // #7E4CC9
  darkPink: CropCircleColors.label.magenta, // #D649A6

  // mentions list
  mentionName: CropCircleColors.system.textPrimary, // #2F2F2F
  mentionEmail: CropCircleColors.system.textSecondary, // #6C6C6C
};

export const Colors = {
  ...LegacyColors,
};

type ColorSource = Record<
  string,
  string | Record<number, string> | Record<string, string>
>;

export function applyColors(source: ColorSource) {
  Object.keys(Colors).forEach((key) => {
    delete (Colors as ColorSource)[key];
  });

  Object.assign(Colors, source);
}

export { LegacyColors, CropCircleColorsMap };
